import React, { useCallback, useEffect, useState } from 'react';
import { Clock, CheckCircle2, XCircle, Undo2 } from 'lucide-react';
import { useApi } from '../../../lib/api';
import PartnersSubNav from './PartnersSubNav';
import ReasonModal from '../../../components/shared/ReasonModal';
import Pagination from '../../../components/shared/Pagination';
import { SearchableSelect } from '../../../components/workspace/filters/FilterDropdowns';
import '../../../styles/Dashboard.css';
import '../../../styles/workspace.css';
import '../../../styles/PartnerConsole.css';

const PAGE_SIZE = 10;

const STATUS_LABELS = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
};

const STATUS_COLORS = {
  pending:   { bg: '#fff4e5', fg: '#b45309' },
  approved:  { bg: '#eafaf0', fg: '#16a34a' },
  rejected:  { bg: '#fdecea', fg: '#b42318' },
  withdrawn: { bg: '#f3f4f6', fg: '#6b7280' },
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || { bg: '#f3f4f6', fg: '#6b7280' };
  return (
    <span className="pc-pill" style={{ background: c.bg, color: c.fg }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
      <span className="muted" style={{ fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 14, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{value || '—'}</span>
    </div>
  );
}

// Full application details. For a pending application it also lets the admin
// pick the starting tier (otherwise the partner earns nothing until one is
// assigned) and approve or reject from here.
function ApplicationModal({ app, tiers, busy, onApprove, onReject, onClose }) {
  const [tierId, setTierId] = useState('');
  const isPending = app.status === 'pending';
  return (
    <div className="modal-backdrop" onClick={() => !busy && onClose()}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2 style={{ overflowWrap: 'anywhere' }}>{app.company_name}</h2>
          <button className="modal-close" onClick={onClose} disabled={busy}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 12 }}><StatusBadge status={app.status} /></div>
          <DetailRow label="Contact name" value={app.contact_name} />
          <DetailRow label="Contact email" value={app.contact_email} />
          <DetailRow label="Contact phone" value={app.contact_phone} />
          <DetailRow label="Account email" value={app.user_email} />
          <DetailRow label="Website" value={app.website} />
          <DetailRow label="Country" value={app.country} />
          <DetailRow label="PAN / Tax ID" value={app.pan_or_tax_id} />
          <DetailRow label="Notes" value={app.notes} />
          <DetailRow label="Applied" value={new Date(app.created_at).toLocaleString()} />
          {app.decided_at && <DetailRow label="Decided" value={new Date(app.decided_at).toLocaleString()} />}
          {app.decision_reason && <DetailRow label="Rejection reason" value={app.decision_reason} />}

          {isPending && (
            <>
              <div className="form-row" style={{ marginTop: 16 }}>
                <label>Starting tier</label>
                <SearchableSelect
                  value={tierId}
                  onChange={setTierId}
                  options={[
                    { value: '', label: 'Not Assigned (no commission until set)' },
                    ...tiers.filter((t) => t.is_active).map((t) => ({ value: String(t.id), label: `${t.name} (${t.commission_percentage}%)` })),
                  ]}
                  showAll={false}
                  btnClass="form-select-btn"
                  matchTriggerWidth
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button className="btn-secondary" onClick={onReject} disabled={busy}>Reject</button>
                <button className="btn-primary" onClick={() => onApprove(tierId ? Number(tierId) : null)} disabled={busy}>
                  {busy ? 'Approving…' : 'Approve'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PartnerApplicationsPage() {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [viewApp, setViewApp] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [page, setPage] = useState(1);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await api.adminListPartnerApplications(statusFilter || undefined);
      setItems(res.items || []);
      setCounts(res.counts || {});
      setPage(1);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    api.adminListPartnerTiers().then((r) => setTiers(r.items || [])).catch(() => {});
  }, [api]);

  const handleApprove = async (id, tierId = null) => {
    setBusyId(id);
    try {
      await api.adminApprovePartnerApplication(id, tierId);
      setViewApp(null);
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (reason) => {
    setBusyId(rejectId);
    try {
      await api.adminRejectPartnerApplication(rejectId, reason);
      setRejectId(null);
      setViewApp(null);
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusyId(null);
    }
  };

  const pagedItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const showActions = pagedItems.some((it) => it.status === 'pending');
  const wrapCell = { overflowWrap: 'anywhere', wordBreak: 'break-word', verticalAlign: 'middle' };

  return (
    <div>
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Partner Applications</h1>
          <p className="admin-page-subtitle">Review and decide partner applications.</p>
        </div>
      </div>
      <PartnersSubNav />

      <div className="admin-kpi-row">
        <div className="admin-kpi">
          <div className="admin-kpi-icon orange"><Clock size={18} /></div>
          <p className="admin-kpi-label">Pending</p>
          <p className="admin-kpi-value">{counts.pending || 0}</p>
        </div>
        <div className="admin-kpi ok">
          <div className="admin-kpi-icon green"><CheckCircle2 size={18} /></div>
          <p className="admin-kpi-label">Approved</p>
          <p className="admin-kpi-value">{counts.approved || 0}</p>
        </div>
        <div className="admin-kpi">
          <div className="admin-kpi-icon red"><XCircle size={18} /></div>
          <p className="admin-kpi-label">Rejected</p>
          <p className="admin-kpi-value">{counts.rejected || 0}</p>
        </div>
        <div className="admin-kpi">
          <div className="admin-kpi-icon purple"><Undo2 size={18} /></div>
          <p className="admin-kpi-label">Withdrawn</p>
          <p className="admin-kpi-value">{counts.withdrawn || 0}</p>
        </div>
      </div>

      <div className="admin-card-toolbar" style={{ justifyContent: 'flex-end' }}>
        <SearchableSelect
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
            { value: 'withdrawn', label: 'Withdrawn' },
            { value: '', label: 'All' },
          ]}
          showAll={false}
          btnClass="form-select-btn"
          wrapStyle={{ width: 220 }}
          matchTriggerWidth
        />
      </div>

      {error && <div className="admin-banner error">{error}</div>}
      {loading ? (
        <p className="muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="muted">No {statusFilter || ''} applications.</p>
      ) : (
        <>
          <div className="entity-table-wrap">
            <table className="entity-table" style={{ width: '100%', minWidth: 820 }}>
              <colgroup>
                <col style={{ width: '24%' }} />
                <col />
                <col style={{ width: 120 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: showActions ? 260 : 100 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Applied</th>
                  <th>Status</th>
                  <th className="row-actions" style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((it) => (
                  <tr key={it.id}>
                    <td style={wrapCell}>{it.company_name}</td>
                    <td style={wrapCell}>
                      <div>{it.contact_name}</div>
                      <div className="muted" style={{ fontSize: 13 }}>{it.contact_email}</div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{new Date(it.created_at).toLocaleDateString()}</td>
                    <td style={{ verticalAlign: 'middle' }}>
                      <StatusBadge status={it.status} />
                    </td>
                    <td className="row-actions" style={{ verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button className="btn-link-sm" onClick={() => setViewApp(it)}>View</button>
                        {it.status === 'pending' && (
                          <>
                            <button className="btn-primary" onClick={() => setViewApp(it)} disabled={busyId === it.id}>
                              Approve
                            </button>
                            <button className="btn-secondary" onClick={() => setRejectId(it.id)} disabled={busyId === it.id}>
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalItems={items.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}

      {viewApp && rejectId == null && (
        <ApplicationModal
          app={viewApp}
          tiers={tiers}
          busy={busyId === viewApp.id}
          onApprove={(tierId) => handleApprove(viewApp.id, tierId)}
          onReject={() => setRejectId(viewApp.id)}
          onClose={() => setViewApp(null)}
        />
      )}

      {rejectId != null && (
        <ReasonModal
          title="Reject Partner Application"
          label="Reason for rejection"
          confirmLabel="Confirm Reject"
          busy={busyId === rejectId}
          onConfirm={handleReject}
          onClose={() => setRejectId(null)}
        />
      )}
    </div>
  );
}
